import React, { useEffect, useState } from "react";
import MaterialTable, { Column } from "@material-table/core";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Checkbox, FormControlLabel, MenuItem, TextField } from "@mui/material";
import { FilterAltOutlined, Search } from "@mui/icons-material";
import MenuBox from "./MenuBox";
import '../style/Components.css';
import useTableStore from "../stores/TableStore";
import SelectBox from "./SelectBox";
import PaginationBox from "./PaginationBox";
import usePaginationStore from "../stores/PaginationStore";

export interface RowData {
    [key: string]: any;
}

interface NewTableBoxProps {
    columns: EnhancedColumn<RowData>[]; // 自訂的欄位類型
    data: RowData[]; // 資料內容
    showEdit?: boolean;
    editFunction?: (rowData: any) => void;
    showDelete?: boolean;
    deleteFunction?: (rowData: any) => void;
    showSelection?: boolean;
    showDisplayHeader?: boolean;
    renderMoreContent?: (rowData: any) => React.ReactNode;
    tableRef?: React.RefObject<any>;
    totalCount?: number;
}

interface EnhancedColumn<T extends RowData> extends Column<T> {
    isLink?: boolean;
    linkHandler?: (value: any, row: RowData) => void;
    isSort?: boolean;
    isSearch?: boolean;
    isFilter?: boolean;
    filterOptions?: { value: string | number; label: string }[];
    showSearchText?: boolean;
    render?: (rowData: RowData) => React.ReactNode;
}

const NewTableBox: React.FC<NewTableBoxProps> = ({
    columns,
    data,
    showEdit = false,
    editFunction,
    showDelete = false,
    deleteFunction,
    showSelection = false,
    showDisplayHeader = false,
    renderMoreContent,
    tableRef,
    totalCount,
}) => {
    const { setRemoveIdArray } = useTableStore();
    const [searchTexts, setSearchTexts] = useState<Record<string, string>>({});
    const [showSearchText, setShowSearchText] = useState<Record<string, boolean>>({});
    const [visibleColumns, setVisibleColumns] = useState<string[]>(columns.map(col => col.field as string));

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const { setBeforeCursor, setAfterCursor, setLimit, reQuery, setPageAction } = usePaginationStore();

    const handlePageChange = (eventType: string, value: any) => {
        if (eventType == 'pageChange') {
            setPage(page + value);

            if (value > 0) {
                setBeforeCursor('');
            } else if (value < 0) {
                setAfterCursor('');
            } else {
                setBeforeCursor('');
                setAfterCursor('');
                setPage(0);
            }

        } else if (eventType == 'pageSize') {
            setBeforeCursor('');
            setAfterCursor('');
            setRowsPerPage(value);
            setLimit(value);
        }

        setPageAction(true);
    }

    const handleSearchChange = (field: string, value: string) => {
        setSearchTexts((prev) => ({ ...prev, [field]: value }));
    };

    const handleToggleSearch = (field: string) => {
        setShowSearchText((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleColumnToggle = (field: string) => {
        setVisibleColumns((prev) =>
            prev.includes(field) ? prev.filter((col) => col !== field) : [...prev, field]
        );
    };

    const filteredData = data.filter((row) => {
        return columns.every((col) => {
            const searchText = searchTexts[col.field as string];
            if (!searchText) return true;
            const cellValue = row[col.field as keyof RowData];
            return (
                cellValue &&
                cellValue.toString().toLowerCase().includes(searchText.toLowerCase())
            );
        });
    });

    const setSelectedRows = (rows: any) => {
        const selectedIds = rows.map((row: any) => row.id);
        setRemoveIdArray(selectedIds);
    }

    const enhancedColumns: Column<RowData>[] = columns.filter((col) =>
        visibleColumns.includes(col.field as string)
    ).map((col) => ({
        ...col,
        title: (
            <>
                {col.isSearch ? (
                    <div>
                        {showSearchText[col.field as string] ? (
                            <TextField
                                value={searchTexts[col.field as string] || ""}
                                placeholder={`Search ${col.title}`}
                                variant="standard"
                                size="small"
                                style={{ width: "100px" }}
                                onChange={(e) => handleSearchChange(col.field as string, e.target.value)}
                            />
                        ) : (
                            col.title
                        )}
                        <Search
                            fontSize="small"
                            style={{ marginLeft: 8, cursor: "pointer" }}
                            onClick={() => handleToggleSearch(col.field as string)}
                        />
                    </div>
                ) : col.isFilter ? (
                    <div>
                        {showSearchText[col.field as string] ? (
                            <SelectBox
                                name="valueType"
                                value={searchTexts[col.field as string] || ""}
                                options={col.filterOptions || []}
                                onChange={(e) => handleSearchChange(col.field as string, e.target.value)}
                            />
                        ) : (
                            col.title
                        )}
                        <FilterAltOutlined
                            fontSize="small"
                            style={{ marginLeft: 8, cursor: "pointer" }}
                            onClick={() => handleToggleSearch(col.field as string)}
                        />
                    </div>
                ) : (
                    col.title
                )}
            </>
        ),
        filtering: showSearchText[col.field as string],
        sorting: col.isSort !== undefined ? col.isSort : false,
        render: col.isLink
            ? (rowData) => (
                <Typography
                    color="primary"
                    component="a"
                    onClick={(e) => {
                        e.preventDefault();
                        col.linkHandler && col.linkHandler(rowData[col.field as keyof RowData], rowData);
                    }}
                    style={{ cursor: "pointer" }}
                >
                    {rowData[col.field as keyof RowData]}
                </Typography>
            )
            : col.render,
    }));

    if (showEdit || showDelete || renderMoreContent) {
        enhancedColumns.push({
            field: "actions",
            title: showDisplayHeader ? (
                <MenuBox>
                    {columns.map((col) => (
                        col.title !== '' &&
                        <MenuItem key={col.field as string}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={visibleColumns.includes(col.field as string)}
                                        onChange={() => handleColumnToggle(col.field as string)}
                                    />
                                }
                                label={col.title}
                            />
                        </MenuItem>
                    ))}
                </MenuBox>
            ) : (''),
            sorting: false,
            render: (rowData) => (
                <>
                    {showEdit && (
                        <IconButton onClick={() => editFunction?.(rowData)}>
                            <EditIcon />
                        </IconButton>
                    )}
                    {showDelete && (
                        <IconButton onClick={() => deleteFunction?.(rowData)}>
                            <DeleteOutlineOutlinedIcon />
                        </IconButton>
                    )}
                    {renderMoreContent && renderMoreContent(rowData)}
                </>
            ),
        });
    }

    useEffect(() => {
        if (reQuery) {
            setPage(0);
        }
    }, [reQuery]);

    return (
        <MaterialTable
            tableRef={tableRef}
            columns={enhancedColumns}
            data={filteredData}
            options={{
                search: false,
                toolbar: false,
                selection: showSelection,
                pageSize: rowsPerPage,
                pageSizeOptions: [5, 10, 25],
                paginationType: "stepped",
                actionsColumnIndex: -1,
            }}
            onSelectionChange={(rows) => setSelectedRows(rows)}
            components={{
                Pagination: (props) => (
                    <PaginationBox
                        {...props}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        count={totalCount ? totalCount : filteredData.length}
                        onPageChange={(o: any) => handlePageChange('pageChange', o)}
                        onRowsPerPageChange={(o: any) => handlePageChange('pageSize', o)}
                    />
                ),
            }}
        />
    );
};

export default NewTableBox;
